import { useLocalStorage } from 'react-use';

import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';

import LaunchpadPromoCard from 'components/launchpad/LaunchpadPromoCard';
import RcsGraphic1 from 'img/rcs-graphic-1.webp';
import RcsGraphic2 from 'img/rcs-graphic-2.webp';
import RcsGraphic3 from 'img/rcs-graphic-3.webp';

import {
  RCS_BUILD_PROFILE_STEP_KEY,
  RCS_CREATE_SENDER_STEP_KEY,
  RCS_FEATURE_KEY,
  RCS_REQUEST_LAUNCH_STEP_KEY,
  RCS_SEND_TEST_STEP_KEY,
} from 'providers/ProgressiveOnboarding/constants/constants';

import { ButtonLink } from '@postscript/components';
import type {
  PromoCardSlotProps,
  Step,
  WorkflowContent,
} from 'providers/ProgressiveOnboarding/types';

const RcsPromoCard = ({ onDismiss }: PromoCardSlotProps) => {
  const { allGuides, setActiveGuide } = useProgressiveOnboarding();
  const [, setSplashDismissed] = useLocalStorage(
    `progressive_onboarding_${RCS_FEATURE_KEY}_splash_dismissed`,
    false,
  );

  const handleLearnMore = () => {
    // Always show the splash on Learn More, even if previously dismissed.
    setSplashDismissed(false);
    const rcsGuide = allGuides.find((g) => g.key === RCS_FEATURE_KEY);
    if (rcsGuide) setActiveGuide(rcsGuide);
  };

  return (
    <LaunchpadPromoCard
      title="Create RCS Profile"
      description="Before you can send RCS messages, Google requires a verified sender profile for your brand. It's what subscribers see when they receive a message from you."
      tags={[{ label: 'New Feature', variant: 'success' }]}
      onLearnMore={handleLearnMore}
      onDismiss={onDismiss}
      imageUrl={RcsGraphic1}
    />
  );
};

const RcsPromoCardTest = ({ onDismiss }: PromoCardSlotProps) => {
  const { allGuides, setActiveGuide } = useProgressiveOnboarding();

  const handleLearnMore = () => {
    const rcsGuide = allGuides.find((g) => g.key === RCS_FEATURE_KEY);
    if (rcsGuide) setActiveGuide(rcsGuide);
  };

  return (
    <LaunchpadPromoCard
      title="Test RCS"
      description="Next, you'll test RCS on your device. To ensure your device can receive RCS messages, you'll register your device as an RCS Test Device. You can add as many test devices as you'd like."
      tags={[{ label: 'New Feature', variant: 'success' }]}
      onLearnMore={handleLearnMore}
      onDismiss={onDismiss}
      imageUrl={RcsGraphic2}
    />
  );
};

const RcsPromoCardLaunch = ({ onDismiss }: PromoCardSlotProps) => {
  const { allGuides, setActiveGuide } = useProgressiveOnboarding();

  const handleLearnMore = () => {
    const rcsGuide = allGuides.find((g) => g.key === RCS_FEATURE_KEY);
    if (rcsGuide) setActiveGuide(rcsGuide);
  };

  return (
    <LaunchpadPromoCard
      title="RCS Launch Application"
      description="Once you're satisfied with your RCS Profile, complete the application form by clicking the button below."
      tags={[
        { label: 'New Feature', variant: 'success' },
        { label: 'Update', variant: 'info' },
      ]}
      onLearnMore={handleLearnMore}
      onDismiss={onDismiss}
      imageUrl={RcsGraphic3}
    />
  );
};

const RcsGoToStep = ({ step }: { step: Step }) => {
  return (
    <ButtonLink to={step.link}>
      {step.state === 'complete' ? 'Review' : 'Begin'}
    </ButtonLink>
  );
};

export const RCS_STEPS: WorkflowContent['steps'] = {
  [RCS_BUILD_PROFILE_STEP_KEY]: {
    key: RCS_BUILD_PROFILE_STEP_KEY,
    guideKey: RCS_FEATURE_KEY,
    title: 'Build Your Profile',
    icon: 'Edit',
    content:
      'Enter your brand details, logo, and contact information to set up your RCS sender profile.',
    link: '/account/settings/rcs/profile',
    PrimaryAction: RcsGoToStep,
    promoCard: RcsPromoCard,
  },
  [RCS_CREATE_SENDER_STEP_KEY]: {
    key: RCS_CREATE_SENDER_STEP_KEY,
    guideKey: RCS_FEATURE_KEY,
    title: 'Create Your Sender',
    icon: 'Upload',
    content:
      'Review your profile settings and submit to create your sender so you can begin testing.',
    link: '/account/settings/rcs/profile/review',
    PrimaryAction: RcsGoToStep,
  },
  [RCS_SEND_TEST_STEP_KEY]: {
    key: RCS_SEND_TEST_STEP_KEY,
    guideKey: RCS_FEATURE_KEY,
    title: 'Send a Test Message',
    icon: 'RcsCarousel',
    content:
      'Add test phone numbers and send a live RCS message to see exactly how your profile appears to recipients.',
    link: '/account/settings/rcs/testing',
    PrimaryAction: RcsGoToStep,
    promoCard: RcsPromoCardTest,
  },
  [RCS_REQUEST_LAUNCH_STEP_KEY]: {
    key: RCS_REQUEST_LAUNCH_STEP_KEY,
    guideKey: RCS_FEATURE_KEY,
    title: 'Submit Launch Request',
    icon: 'Rocket',
    content:
      'Provide additional details about your target carriers, expected messaging volume, and use case to submit your sender for launch approval.',
    link: '/account/settings/rcs/request-launch',
    PrimaryAction: RcsGoToStep,
    promoCard: RcsPromoCardLaunch,
  },
};
